<template>
    <b-modal
        :visible="true"
        size="lg"
        title="평가 프로파일 비교 실행"
        @hidden="$emit('close')"
    >
        <p class="text-muted">
            같은 문항에 두 프로파일(또는 기본 vs 다른 평가자 기준)로 AI 평가를 각각 요청합니다.
            평가는 비동기로 처리되며, 두 결과를 프로파일별로 나란히 보는 화면은 아직 없습니다.
        </p>

        <b-form>
            <b-row>
                <b-col md="4">
                    <b-form-group label="캠페인 ID">
                        <b-form-input v-model.number="campaignId" type="number" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="기업 ID">
                        <b-form-input v-model.number="companyId" type="number" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="문항 코드 (선택)">
                        <b-form-input v-model="subCode" placeholder="비우면 전체 문항 대상" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-card no-body class="p-3 mb-3">
                <b-row>
                    <b-col md="6">
                        <b-form-group label="프로파일 A (기본/기준)">
                            <b-form-input :value="baseProfile.profile_name" readonly />
                            <small class="text-muted">id: {{ baseProfile.id }} · {{ baseProfile.status }}</small>
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="프로파일 B (비교 대상)">
                            <b-form-select v-model="compareProfileId" :options="otherProfileOptions" />
                        </b-form-group>
                    </b-col>
                </b-row>
            </b-card>
        </b-form>

        <b-alert :show="!!message" :variant="messageVariant">{{ message }}</b-alert>

        <template #modal-footer>
            <b-button variant="secondary" @click="$emit('close')">닫기</b-button>
            <b-button
                variant="primary"
                :disabled="running || requested || !canRun"
                @click="runCompare"
            >
                <b-spinner v-if="running" small /> {{ requested ? '요청 완료' : '비교 실행' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import { getErrorMessage } from '@core/utils/api/errorMessage';
export default {
    name: 'EvalProfileCompare',
    props: {
        baseProfile: { type: Object, required: true },
        profiles: { type: Array, default: () => [] },
    },
    data() {
        return {
            campaignId: null,
            companyId: null,
            subCode: '',
            compareProfileId: null,
            running: false,
            confirming: false,
            // 요청이 접수된 대상 키 — 입력이 바뀌면 키가 달라져 자동으로 '미요청'이 된다.
            // A 만 접수되고 B 가 실패했을 때 다시 눌러도 A 는 다시 발행하지 않는다.
            doneAKey: null,
            requestedKey: null,
            message: '',
            messageVariant: 'info',
        };
    },
    computed: {
        otherProfileOptions() {
            return [
                { value: null, text: '선택...' },
                ...this.profiles
                    .filter((p) => p.id !== this.baseProfile.id)
                    .map((p) => ({
                        value: p.id,
                        text: `${p.profile_name} (${p.status})`,
                    })),
            ];
        },
        canRun() {
            return this.campaignId && this.companyId && this.compareProfileId;
        },
        targetKey() {
            return JSON.stringify([this.campaignId, this.companyId, this.subCode || '']);
        },
        doneA() {
            return this.doneAKey === this.targetKey;
        },
        // 같은 대상·같은 B 의 중복 발행만 막는다. 대상이나 B 가 바뀌면 다시 요청할 수 있다.
        requested() {
            return this.requestedKey === `${this.targetKey}|${this.compareProfileId}`;
        },
    },
    methods: {
        async runCompare() {
            if (this.running || this.confirming) return;
            // 확인창이 떠 있는 동안 입력이 바뀌어도 확인받은 대상으로만 발행한다.
            const key = this.targetKey;
            const skipA = this.doneA;
            const basePayload = {
                campaign_id: this.campaignId,
                company_id: this.companyId,
                sub_code: this.subCode || undefined,
                profile_id: this.baseProfile.id,
            };
            const comparePayload = { ...basePayload, profile_id: this.compareProfileId };
            const requestedKey = `${key}|${this.compareProfileId}`;
            // 비용이 드는 AI 평가를 발행한다 — 대상과 범위를 확인받는다.
            // ponytail: 서버 dry_run 건수 미확인(BE 필요) — 건수 대신 입력한 대상을 보여준다.
            const scope = this.subCode
                ? `문항 ${this.subCode}`
                : '문항 코드를 비웠으므로 이 기업의 전체 문항';
            const count = skipA
                ? 'AI 평가를 1회(프로파일 B) 발행합니다. 프로파일 A 평가는 이미 접수되어 다시 요청하지 않습니다. '
                : 'AI 평가를 2회(프로파일 A, B) 발행합니다. ';
            this.confirming = true;
            const ok = await this.$showModal({
                type: 'confirm',
                state: 'warning',
                title: '프로파일 비교 실행',
                message: `캠페인 ${basePayload.campaign_id} / 기업 ${basePayload.company_id}의 ${scope}에 대해 ${count}`
                    + '이미 평가된 문항도 다시 평가될 수 있고, AI 비용이 발생하며 요청 후에는 취소할 수 없습니다. 계속하시겠습니까?',
            });
            this.confirming = false;
            if (!ok) return;
            this.running = true;
            this.message = '';
            let aDone = skipA;
            let partialErrors = 0;
            // 200 이어도 전 문항이 errors 면 접수되지 않은 것으로 본다(일부만 실패면 접수 + 건수 안내).
            const post = async (payload) => {
                const { data } = await this.$axios.post('/assessments/ai-audit', payload);
                const errors = (data?.errors || []).length;
                if (errors && !(data?.created || []).length) throw new Error(`${errors}건 모두 요청에 실패했습니다.`);
                partialErrors += errors;
            };
            try {
                // 두 프로파일로 각각 평가 실행 (비동기 큐 적재)
                if (!aDone) {
                    await post(basePayload);
                    aDone = true;
                    this.doneAKey = key;
                }
                await post(comparePayload);

                this.requestedKey = requestedKey;
                this.messageVariant = partialErrors ? 'warning' : 'success';
                this.message = partialErrors
                    ? `두 프로파일로 평가를 요청했습니다. 일부 문항 ${partialErrors}건은 요청에 실패했습니다.`
                    : '두 프로파일로 평가를 요청했습니다.';
            } catch (e) {
                if (e?.response?.status === 401) return;
                this.messageVariant = 'danger';
                const msg = getErrorMessage(e, '') || (!e?.isAxiosError && e?.message) || '';
                this.message = aDone
                    ? `프로파일 A 평가는 접수됐고 B 요청만 실패했습니다. [비교 실행]을 다시 누르면 B 만 요청합니다.${msg ? ` (${msg})` : ''}`
                    : `요청이 접수되지 않았습니다. ${msg || '평가 실행 요청에 실패했습니다.'}`;
            } finally {
                this.running = false;
            }
        },
    },
};
</script>
